// The Mail workspace's Metrics tab (Session AUG, email system Phase 6, Item 2): how the mailbox is being handled, read
// from GET /api/email-engine/metrics, which derives every figure live from what the email engine has recorded and stores
// nothing (RULE 6). Inbound per lift per month, the share filed, proposed as work, deflected, templated and bespoke, the
// median first reply by category, the sent-unchanged rate per template, the modernisation reply time, and the trend by
// month. A share with nothing to divide by reads "Not measured", never 0%: an empty month is not a perfect one.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import type { ChaseMetrics, EmailMetrics } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { Button } from "../../../components/Button";
import { Loader } from "../../../components/Loader";
import { formatMonthYear, londonDateKey } from "../../../lib/format";

export const METRICS_PRESETS = [
  { months: 3, label: "3 months" },
  { months: 6, label: "6 months" },
  { months: 12, label: "12 months" },
] as const;

const chipCls = (on: boolean) =>
  `shrink-0 whitespace-nowrap rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
    on ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
  }`;
const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4";
const thCls = "px-3 py-2";
const tdNum = "px-3 py-2 text-right tabular-nums";

/** Today in London as YYYY-MM-DD. */
export function londonToday(now: Date = new Date()): string {
  return londonDateKey(now);
}

/** The first day of the London month `months - 1` months before today's, so "3 months" is this month and the two before. */
export function presetFrom(months: number, now: Date = new Date()): string {
  const [y, m] = londonToday(now).split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) - (months - 1);
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}-01`;
}

/** A share, or "Not measured" when there was nothing to divide by. */
export function fmtPct(v: number | null | undefined): string {
  return v == null ? "Not measured" : `${v}%`;
}

/** Hours as the office reads them: under two days in hours, otherwise in days. */
export function fmtHours(v: number | null | undefined): string {
  if (v == null) return "No reply on record";
  if (v < 48) return `${v} ${v === 1 ? "hour" : "hours"}`;
  const days = Math.round((v / 24) * 10) / 10;
  return `${days} days`;
}

function fmtMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  return formatMonthYear(new Date(Date.UTC(y, m - 1, 15)));
}

function Tile({ label, value, note, testId }: { label: string; value: string; note?: string; testId: string }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3" data-testid={testId}>
      <p className="font-menu text-xs uppercase tracking-wide text-ele-body">{label}</p>
      <p className="mt-1 font-heading text-xl text-ele-strong">{value}</p>
      {note && <p className="mt-1 text-xs text-ele-body">{note}</p>}
    </div>
  );
}

export function MetricsTab() {
  const [preset, setPreset] = useState<number | null>(6);
  const [from, setFrom] = useState(() => presetFrom(6));
  const [to, setTo] = useState(() => londonToday());
  const [data, setData] = useState<EmailMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.emailEngine.metrics({ from, to }));
    } catch (err) {
      setData(null);
      setError(friendlyApiError(err, "Could not load the email metrics."));
    } finally {
      setLoading(false);
    }
  }, [from, to]);
  useEffect(() => {
    void load();
  }, [load]);

  function choosePreset(months: number) {
    setPreset(months);
    setFrom(presetFrom(months));
    setTo(londonToday());
  }

  const categories = (data?.categories ?? []).filter((c) => c.total > 0);
  const m = data?.modernisation;

  return (
    <div className="min-w-0" data-testid="mail-metrics">
      <p className="mb-3 text-sm text-ele-body">
        How the mailbox is being handled, worked out fresh from what the email engine has recorded each time this page opens.
      </p>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        {METRICS_PRESETS.map((p) => (
          <button key={p.months} type="button" aria-pressed={preset === p.months} onClick={() => choosePreset(p.months)} className={chipCls(preset === p.months)}>
            {p.label}
          </button>
        ))}
        <label className="ml-2 text-xs text-ele-body">
          From
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => {
              setPreset(null);
              setFrom(e.target.value);
            }}
            className="ml-1 rounded-ele border border-ele-purple-accent bg-ele-inset px-2 py-1 text-sm text-ele-strong max-sm:min-h-11"
            aria-label="From"
          />
        </label>
        <label className="text-xs text-ele-body">
          To
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => {
              setPreset(null);
              setTo(e.target.value);
            }}
            className="ml-1 rounded-ele border border-ele-purple-accent bg-ele-inset px-2 py-1 text-sm text-ele-strong max-sm:min-h-11"
            aria-label="To"
          />
        </label>
      </div>

      {error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{error}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : loading && !data ? (
        <Loader label="Loading the metrics..." />
      ) : data ? (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Tile testId="metric-received" label="Received" value={String(data.totals.messages)} note={`${data.activeLifts.count} lifts under contract`} />
            <Tile
              testId="metric-no-person"
              label="Settled without a person"
              value={fmtPct(data.handling.settledWithoutPersonPct ?? null)}
              note={
                data.handling.settledComposition
                  ? `${data.handling.settledWithoutPerson ?? 0} of ${data.handling.settledComposition.liveReceived} live: ${data.handling.settledComposition.filed} filed, ${data.handling.settledComposition.alreadyAnswered} already answered, ${data.handling.settledComposition.internalOrGone} internal or gone, ${data.handling.settledComposition.automatedOrOther} other`
                  : `${data.handling.settledWithoutPerson ?? 0} filed or settled with no reply needed`
              }
            />
            <Tile testId="metric-filed" label="Filed" value={fmtPct(data.handling.filedPct)} note={`${data.handling.filed} filed, ${data.handling.work} work orders (${fmtPct(data.handling.workPct)})`} />
            <Tile testId="metric-templated" label="Templated" value={fmtPct(data.handling.templatedPct)} note={`of ${data.handling.owedReply} owed a reply`} />
            <Tile testId="metric-bespoke" label="Bespoke" value={fmtPct(data.handling.bespokePct)} note={`${data.handling.freeDraft} free drafts, ${data.handling.person} for a person`} />
            <Tile testId="metric-deflected" label="Deflected" value={fmtPct(data.deflection.deflectedPct)} note={`${data.deflection.portalFaultReports} portal fault reports, ${data.deflection.noticesSent} notices sent`} />
            <Tile testId="metric-first-reply" label="Median first reply" value={fmtHours(data.totals.medianHoursToFirstReply)} note={`${data.totals.replied} of ${data.totals.messages} replied on record`} />
            <Tile
              testId="metric-modernisation"
              label="Modernisation reply"
              value={fmtHours(m?.medianHours ?? null)}
              note={m ? `${m.repliedWithinTarget} within ${m.targetHours} hours, ${m.repliedAfterTarget} later, ${m.unansweredPastTarget} unanswered past it` : undefined}
            />
            <Tile testId="metric-pending" label="Still deciding" value={String(data.handling.pending)} note="messages waiting for their outcome" />
          </div>

          <section className={sectionCls}>
            <h2 className="font-heading text-lg text-ele-strong">Trend by month</h2>
            <div className="mt-3 overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm" data-testid="metrics-months">
                <colgroup>
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "9%" }} />
                  <col className="max-md:hidden" style={{ width: "8%" }} />
                  <col className="max-md:hidden" style={{ width: "11%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col className="max-md:hidden" style={{ width: "9%" }} />
                  <col className="max-md:hidden" style={{ width: "9%" }} />
                  <col style={{ width: "11%" }} />
                  <col className="max-md:hidden" style={{ width: "11%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <th className={thCls}>Month</th>
                    <th className={`${thCls} text-right`}>Received</th>
                    <th className={`max-md:hidden ${thCls} text-right`}>Per lift</th>
                    <th className={`max-md:hidden ${thCls} text-right`}>Settled</th>
                    <th className={`${thCls} text-right`}>Filed</th>
                    <th className={`${thCls} text-right`}>Templated</th>
                    <th className={`max-md:hidden ${thCls} text-right`}>Bespoke</th>
                    <th className={`max-md:hidden ${thCls} text-right`}>Deflected</th>
                    <th className={`${thCls} text-right`}>First reply</th>
                    <th className={`max-md:hidden ${thCls} text-right`}>Modernisation</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((row) => (
                    <tr key={row.month} className="border-t border-ele-purple-accent" data-testid={`metrics-month-${row.month}`}>
                      <td className="truncate px-3 py-2">{fmtMonth(row.month)}</td>
                      <td className={tdNum}>{row.total}</td>
                      <td className={`max-md:hidden ${tdNum}`}>{row.perActiveLift ?? "Not measured"}</td>
                      <td className={`max-md:hidden ${tdNum}`}>{fmtPct(row.shares.settledWithoutPersonPct ?? null)}</td>
                      <td className={tdNum}>{fmtPct(row.shares.filedPct)}</td>
                      <td className={tdNum}>{fmtPct(row.shares.templatedPct)}</td>
                      <td className={`max-md:hidden ${tdNum}`}>{fmtPct(row.shares.bespokePct)}</td>
                      <td className={`max-md:hidden ${tdNum}`}>{fmtPct(row.deflectedPct)}</td>
                      <td className={`truncate ${tdNum}`}>{fmtHours(row.medianHoursToFirstReply)}</td>
                      <td className={`max-md:hidden truncate ${tdNum}`}>{fmtHours(row.modernisationMedianHours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-ele-body">{data.handling.basis}</p>
            <p className="mt-1 text-xs text-ele-body">{data.deflection.basis}</p>
            {data.reachedAPerson && data.reachedAPerson.total > 0 && (
              <p className="mt-1 text-xs text-ele-body" data-testid="metrics-reached-a-person">
                Of the {data.reachedAPerson.total} that reached a person:{" "}
                {data.reachedAPerson.byShape.filter((s) => s.count > 0).map((s) => `${s.count} ${s.label.toLowerCase()}`).join(", ") || "none was one of the shapes counted"}
                {data.reachedAPerson.byShape.some((s) => s.count > 0) ? `, ${data.reachedAPerson.otherShape} other` : ""}.
              </p>
            )}
            <p className="mt-1 text-xs text-ele-body">{data.activeLifts.basis}</p>
          </section>

          <section className={sectionCls}>
            <h2 className="font-heading text-lg text-ele-strong">Median first reply by kind of email</h2>
            {categories.length === 0 ? (
              <p className="mt-2 text-sm text-ele-body">No messages were recorded in this range.</p>
            ) : (
              <div className="mt-3 overflow-hidden rounded-ele border border-ele-purple-accent">
                <table className="ele-list-table w-full text-left text-sm" data-testid="metrics-categories">
                  <colgroup>
                    <col style={{ width: "46%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "26%" }} />
                  </colgroup>
                  <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                    <tr>
                      <th className={thCls}>Kind of email</th>
                      <th className={`${thCls} text-right`}>Received</th>
                      <th className={`${thCls} text-right`}>Replied</th>
                      <th className={`${thCls} text-right`}>Median first reply</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => (
                      <tr key={c.category} className="border-t border-ele-purple-accent">
                        <td className="truncate px-3 py-2" title={c.label}>
                          {c.label}
                        </td>
                        <td className={tdNum}>{c.total}</td>
                        <td className={tdNum}>{c.firstReply.replied}</td>
                        <td className={`truncate ${tdNum}`}>{fmtHours(c.firstReply.medianHours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={sectionCls}>
            <h2 className="font-heading text-lg text-ele-strong">Sent unchanged, per template</h2>
            <p className="mt-1 text-xs text-ele-body">
              For the template drafts made in this range: of those sent, how many went exactly as drafted, and how many with only small edits.
            </p>
            {data.templates.length === 0 ? (
              <p className="mt-2 text-sm text-ele-body">No drafts were made from a template in this range.</p>
            ) : (
              <div className="mt-3 overflow-hidden rounded-ele border border-ele-purple-accent">
                <table className="ele-list-table w-full text-left text-sm" data-testid="metrics-templates">
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col className="max-md:hidden" style={{ width: "16%" }} />
                  </colgroup>
                  <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                    <tr>
                      <th className={thCls}>Template</th>
                      <th className={`${thCls} text-right`}>Drafts</th>
                      <th className={`${thCls} text-right`}>Sent unchanged</th>
                      <th className={`${thCls} text-right`}>Unchanged or small edits</th>
                      <th className={`max-md:hidden ${thCls} text-right`}>Not yet sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.templates.map((t) => (
                      <tr key={`${t.key}-${t.version ?? "x"}`} className="border-t border-ele-purple-accent" data-testid={`metrics-template-${t.key}-${t.version ?? "x"}`}>
                        <td className="truncate px-3 py-2" title={t.key}>
                          {t.title ?? t.key}
                          {t.version != null && <span className="text-xs text-ele-body"> · version {t.version}</span>}
                        </td>
                        <td className={tdNum}>{t.drafts}</td>
                        <td className={tdNum}>{fmtPct(t.sentUnchangedPct)}</td>
                        <td className={tdNum}>{fmtPct(t.sentUnchangedOrSmallEditsPct)}</td>
                        <td className={`max-md:hidden ${tdNum}`}>{t.notYetSent}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {m && (
            <section className={sectionCls} data-testid="metrics-modernisation">
              <h2 className="font-heading text-lg text-ele-strong">Modernisation and new installation enquiries</h2>
              <p className="mt-2 text-sm text-ele-body">
                {m.received === 0
                  ? "None were received in this range."
                  : `${m.received} received. ${m.repliedWithinTarget} had a first reply within ${m.targetHours} hours and ${m.repliedAfterTarget} later; ${m.unansweredPastTarget} ${
                      m.unansweredPastTarget === 1 ? "has" : "have"
                    } no reply on record after ${m.targetHours} hours${m.unansweredWithinTarget > 0 ? `, and ${m.unansweredWithinTarget} arrived within the last ${m.targetHours} hours` : ""}. Median first reply: ${fmtHours(m.medianHours)}. Within target: ${fmtPct(m.withinTargetPct)}.`}
              </p>
            </section>
          )}

          <ChaseMetricsSection from={from} to={to} />

          <p className="text-xs text-ele-body">{data.coverage.note}</p>
        </>
      ) : null}
    </div>
  );
}

/**
 * Session AUH (Phase 7, item 6): how chasing is working, per ladder and step, over the same range. Derived on every read from
 * the chases' own send history (server/src/modules/chases/metrics.ts). A rate with nothing to divide by is "Not measured".
 */
function ChaseMetricsSection({ from, to }: { from: string; to?: string }) {
  const [data, setData] = useState<ChaseMetrics | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setFailed(false);
    Promise.resolve()
      .then(() => api.chases.metrics(from, to))
      .then((d) => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [from, to]);
  const rate = (r: number | null) => (r == null ? "Not measured" : `${Math.round(r * 100)}%`);
  return (
    <section className={sectionCls} data-testid="metrics-chases">
      <h2 className="font-heading text-lg text-ele-strong">Chasing</h2>
      {failed ? (
        <p role="alert" className="mt-2 text-sm text-ele-strong">
          The chase figures could not be read.
        </p>
      ) : !data ? (
        <Loader shape="inline" className="mt-2" />
      ) : (
        <>
          <p className="mt-2 text-sm text-ele-body">
            {data.open} open, {data.dueNow} due now, {data.calls} to phone. In this range: {data.totals.sent} sent, replies within 5 working days{" "}
            {rate(data.totals.replyRate)}, {data.totals.resolved} resolved, {data.totals.endedAsCall} ended as a call.
          </p>
          {data.rows.length > 0 && (
            <div data-safetynet className="mt-3 overflow-x-auto">
              <table className="ele-list-table w-full text-left text-sm">
                <thead className="font-menu text-xs uppercase tracking-wide text-ele-blue">
                  <tr>
                    <th className={thCls}>Ladder</th>
                    <th className={`${thCls} text-right`}>Step</th>
                    <th className={`${thCls} text-right`}>Sent</th>
                    <th className={`${thCls} text-right`}>Replied in 5 days</th>
                    <th className={`${thCls} text-right max-md:hidden`}>Resolved</th>
                    <th className={`${thCls} text-right max-md:hidden`}>Ended as a call</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={`${r.ladder}-${r.step}`} className="border-t border-ele-hairline">
                      <td className="px-3 py-2 text-ele-strong">{r.ladderLabel}</td>
                      <td className={tdNum}>{r.step}</td>
                      <td className={tdNum}>{r.sent}</td>
                      <td className={tdNum}>{rate(r.replyRate)}</td>
                      <td className={`max-md:hidden ${tdNum}`}>{r.resolved}</td>
                      <td className={`max-md:hidden ${tdNum}`}>{r.endedAsCall}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
